import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';
import type { EvaluationPlanSheetImageScope } from '../types/evaluationPlanSheetCanvas';
import { forEachBitmapImage } from './slideBitmapImageBlocks';
import {
  deleteEvaluationSheetImageByPublicUrl,
  isEvaluationSheetImageStoragePublicUrl,
  uploadEvaluationSheetImage,
} from '../services/evaluationSheetImageStorageService';

type PendingEntry = {
  blob: Blob;
  mimeType: string;
  objectUrl: string;
  blockId: string;
};

const pendingByObjectUrl = new Map<string, PendingEntry>();
const pendingByBlockId = new Map<string, PendingEntry>();

export function isPendingEvaluationSheetImageSrc(src: string): boolean {
  return src.trim().startsWith('blob:');
}

export function registerPendingEvaluationSheetImage(
  blockId: string,
  blob: Blob,
  mimeType: string
): string {
  revokePendingEvaluationSheetForBlock(blockId);
  const objectUrl = URL.createObjectURL(blob);
  const entry: PendingEntry = { blob, mimeType, objectUrl, blockId };
  pendingByObjectUrl.set(objectUrl, entry);
  pendingByBlockId.set(blockId, entry);
  return objectUrl;
}

export function registerPendingEvaluationSheetClipboardImage(
  blockId: string,
  blob: Blob,
  mimeType: string
): string {
  return registerPendingEvaluationSheetImage(blockId, blob, mimeType);
}

export function clonePendingEvaluationSheetImageForBlock(src: string, newBlockId: string): string | null {
  const trimmed = src.trim();
  if (!trimmed.startsWith('blob:')) return null;
  const entry = pendingByObjectUrl.get(trimmed);
  if (!entry) return null;
  return registerPendingEvaluationSheetImage(newBlockId, entry.blob, entry.mimeType);
}

export function revokePendingEvaluationSheetImageSrc(src: string): void {
  const trimmed = src.trim();
  if (!trimmed.startsWith('blob:')) return;
  const entry = pendingByObjectUrl.get(trimmed);
  if (!entry) return;
  URL.revokeObjectURL(entry.objectUrl);
  pendingByObjectUrl.delete(trimmed);
  pendingByBlockId.delete(entry.blockId);
}

export function revokePendingEvaluationSheetForBlock(blockId: string): void {
  const entry = pendingByBlockId.get(blockId);
  if (entry) revokePendingEvaluationSheetImageSrc(entry.objectUrl);
}

export function revokeAllPendingEvaluationSheetInLayout(layout: ProgramSlideLayoutV1): void {
  forEachBitmapImage(layout, (_kind, img) => {
    const src = img.src?.trim();
    if (src && isPendingEvaluationSheetImageSrc(src)) revokePendingEvaluationSheetImageSrc(src);
  });
}

async function finalizeBitmapImagesArray(
  images: ProgramSlideLayoutV1['clipboardImages'],
  scope: EvaluationPlanSheetImageScope,
  workspaceId?: string | null
): Promise<{ images: ProgramSlideLayoutV1['clipboardImages']; changed: boolean }> {
  if (!images.length) return { images, changed: false };

  let changed = false;
  const next = await Promise.all(
    images.map(async (img) => {
      const src = img.src?.trim() ?? '';
      if (!src || !isPendingEvaluationSheetImageSrc(src)) return img;
      const entry = pendingByObjectUrl.get(src);
      if (!entry) {
        changed = true;
        return { ...img, src: '' };
      }
      const { publicUrl } = await uploadEvaluationSheetImage({
        blob: entry.blob,
        mimeType: entry.mimeType,
        scope,
        workspaceId,
      });
      revokePendingEvaluationSheetImageSrc(src);
      changed = true;
      return { ...img, src: publicUrl };
    })
  );

  return { images: next, changed };
}

export async function finalizePendingEvaluationSheetImagesInLayout(params: {
  layout: ProgramSlideLayoutV1;
  scope: EvaluationPlanSheetImageScope;
  workspaceId?: string | null;
  previousLayout?: ProgramSlideLayoutV1 | null;
}): Promise<ProgramSlideLayoutV1> {
  const { layout, scope, workspaceId, previousLayout } = params;
  const [clipboardResult, scanResult] = await Promise.all([
    finalizeBitmapImagesArray(layout.clipboardImages ?? [], scope, workspaceId),
    finalizeBitmapImagesArray(layout.scanImages ?? [], scope, workspaceId),
  ]);

  if (previousLayout) {
    await releaseRemovedEvaluationSheetImageUrls(previousLayout, layout);
  }

  if (!clipboardResult.changed && !scanResult.changed) return layout;

  return {
    ...layout,
    clipboardImages: clipboardResult.images,
    scanImages: scanResult.images,
  };
}

export async function releaseRemovedEvaluationSheetImageUrls(
  previousLayout: ProgramSlideLayoutV1,
  nextLayout: ProgramSlideLayoutV1
): Promise<void> {
  const prevUrls = new Set<string>();
  const nextUrls = new Set<string>();

  forEachBitmapImage(previousLayout, (_kind, img) => {
    const src = img.src?.trim();
    if (src && isEvaluationSheetImageStoragePublicUrl(src)) prevUrls.add(src);
  });
  forEachBitmapImage(nextLayout, (_kind, img) => {
    const src = img.src?.trim();
    if (src && isEvaluationSheetImageStoragePublicUrl(src)) nextUrls.add(src);
  });

  const removed = Array.from(prevUrls).filter((url) => !nextUrls.has(url));
  await Promise.all(removed.map((url) => deleteEvaluationSheetImageByPublicUrl(url)));
}

/** slide pending helper re-export for mixed usage */
export { releaseRemovedSlideImageUrls } from './slideImagePendingUpload';
